import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/AuthV2`;

// ============================================================
// Servicio de Autenticación (AuthV2)
// Controller: AuthV2Controller
// Ruta base: /api/AuthV2
// Nota: Este es el nuevo sistema de autenticación.
//       El login antiguo (/api/Access/login) sigue funcionando temporalmente.
// ============================================================

/**
 * Login de usuario - Autentica y crea sesión automáticamente.
 * Endpoint público (no requiere JWT).
 * Sistema de bloqueo por intentos fallidos: 5 intentos → bloqueo 30 minutos.
 * Solo se permite una sesión activa por usuario (política HIPAA).
 *
 * @param {Object} credentials
 * @param {string} credentials.email - Correo electrónico del usuario
 * @param {string} credentials.password - Contraseña en texto plano
 *
 * Respuesta exitosa (200):
 * {
 *   "result": 0,
 *   "message": "Login successful",
 *   "token": "eyJhbGciOiJIUzI1NiIs...",    // JWT Bearer token (8 horas, incluye claim session_id)
 *   "employeeId": 74,                        // ID del empleado autenticado
 *   "forcePasswordChange": false,             // true = debe cambiar contraseña antes de usar la app
 *   "passwordExpired": false,                 // true = contraseña expirada (>90 días)
 *   "sessionId": "a7b3c9d2-e5f8-4a1b-..."   // GUID de la sesión creada
 * }
 *
 * Posibles errores (401):
 * { "result": 1, "message": "Correo o contraseña incorrectos" }
 * { "result": 2, "message": "Cuenta inactiva. Contacte al administrador" }
 * { "result": 3, "message": "Cuenta bloqueada hasta 2026-02-10 15:30" }
 *   -- o: "Cuenta bloqueada. Contacte al administrador" (bloqueo permanente)
 * { "result": 4, "message": "Cuenta bloqueada por múltiples intentos fallidos. Intente nuevamente en 30 minutos" }
 * { "result": 5, "message": "Correo o contraseña incorrectos. Intento 3 de 5" }
 *
 * Flujo del frontend al recibir respuesta exitosa:
 * 1. Guardar token en localStorage
 * 2. Si forcePasswordChange=true O passwordExpired=true → redirigir a /change-password
 * 3. Si ambos false → redirigir al dashboard
 */
export const login = async ({ email, password }) => {
  try {
    const response = await axios.post(`${API_URL}/login`, { email, password });
    return response.data;
  } catch (error) {
    // El backend retorna 401 para errores de login con body descriptivo
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en login:", error);
    throw error;
  }
};

/**
 * Cambiar contraseña (voluntario o forzado).
 * Requiere JWT. El employeeId se extrae del token automáticamente.
 *
 * Reglas de complejidad de la nueva contraseña (validadas en backend):
 * - Mínimo 8 caracteres
 * - Al menos 1 mayúscula (A-Z)
 * - Al menos 1 minúscula (a-z)
 * - Al menos 1 número (0-9)
 * - Al menos 1 carácter especial (!@#$%^&*...)
 * - No reutilizar las últimas 6 contraseñas
 * - No puede ser igual a la actual
 *
 * @param {Object} data
 * @param {string} data.currentPassword - Contraseña actual del usuario
 * @param {string} data.newPassword - Nueva contraseña (debe cumplir reglas de complejidad)
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Contraseña actualizada correctamente" }
 *
 * Posibles errores (400):
 * { "result": -1, "message": "La contraseña debe tener al menos 8 caracteres" }
 *   -- (u otro mensaje de validación de complejidad)
 * { "result": 1, "message": "Usuario no encontrado" }
 * { "result": 2, "message": "La contraseña actual es incorrecta" }
 * { "result": 3, "message": "La nueva contraseña no puede ser igual a la actual" }
 * { "result": 4, "message": "No puede reutilizar las últimas 6 contraseñas" }
 */
export const changePassword = async ({ currentPassword, newPassword }) => {
  try {
    const response = await axios.put(
      `${API_URL}/change-password`,
      { currentPassword, newPassword },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en changePassword:", error);
    throw error;
  }
};

/**
 * Solicitar recuperación de contraseña (Olvidé mi contraseña).
 * Endpoint público (no requiere JWT).
 * SIEMPRE retorna el mismo mensaje, sin importar si el correo existe o no
 * (seguridad: previene enumeración de usuarios).
 *
 * ESTADO: Construido pero pendiente - el token se guarda en BD pero el email
 * con el enlace NO se envía aún (pendiente servicio de correo).
 *
 * @param {string} email - Correo electrónico del usuario
 *
 * Respuesta (SIEMPRE 200, mismo mensaje):
 * { "result": 0, "message": "Si el correo está registrado, recibirá un enlace para restablecer su contraseña" }
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await axios.post(
      `${API_URL}/request-password-reset`,
      { email }
    );
    return response.data;
  } catch (error) {
    console.error("Error en requestPasswordReset:", error);
    throw error;
  }
};

/**
 * Restablecer contraseña con token de recuperación.
 * Endpoint público (no requiere JWT).
 * El usuario llega desde el enlace del email: /reset-password?token=...&email=...
 *
 * ESTADO: Construido pero pendiente - depende de que el usuario reciba el token
 * por email (endpoint requestPasswordReset). Cuando se implemente el servicio
 * de correo, este endpoint estará operativo automáticamente.
 *
 * Al resetear exitosamente, el SP automáticamente:
 * - Actualiza contraseña (nuevo hash + salt)
 * - Renueva expiración (+90 días)
 * - Limpia token de reset (no reutilizable)
 * - Desbloquea la cuenta (si estaba bloqueada)
 * - Resetea intentos fallidos a 0
 * - Desactiva cambio forzado
 *
 * @param {Object} data
 * @param {string} data.email - Correo electrónico (viene de la URL del enlace)
 * @param {string} data.token - Token original (viene de la URL del enlace)
 * @param {string} data.newPassword - Nueva contraseña (debe cumplir reglas de complejidad)
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Contraseña restablecida correctamente. Inicie sesión con su nueva contraseña" }
 *
 * Posibles errores (400):
 * { "result": -1, "message": "(Validación de complejidad fallida)" }
 * { "result": 1, "message": "Enlace inválido o expirado" }
 * { "result": 2, "message": "Enlace inválido o expirado" }
 * { "result": 3, "message": "Enlace inválido o expirado" }
 *   -- Nota: result 1,2,3 muestran el MISMO mensaje intencionalmente (seguridad)
 * { "result": 4, "message": "No puede reutilizar su contraseña actual" }
 * { "result": 5, "message": "No puede reutilizar las últimas 6 contraseñas" }
 */
export const resetPassword = async ({ email, token, newPassword }) => {
  try {
    const response = await axios.post(
      `${API_URL}/reset-password`,
      { email, token, newPassword }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en resetPassword:", error);
    throw error;
  }
};
